// IndexedDB wrapper for offline play.
//
// Two object stores:
//   tickets   — unredeemed outcomes ready to play (keyed by ticket_uuid)
//   redeemed  — played tickets pending sync to the server (keyed by ticket_uuid)
//
// We use IndexedDB instead of localStorage so a long-running offline session
// doesn't risk hitting the 5–10 MB cap on a tablet with limited storage.

export interface OfflineTicket {
  ticket_uuid: string;
  won: 0 | 1;
  prize: { id: number; name: string; emoji: string; image_url: string | null } | null;
  expires_at: number; // unix seconds
}

export interface Redemption {
  ticket_uuid: string;
  played_at: number; // unix seconds, client wall clock
}

const DB_NAME = 'wsb_offline_v1';
const DB_VERSION = 1;
const STORE_TICKETS = 'tickets';
const STORE_REDEEMED = 'redeemed';

let dbPromise: Promise<IDBDatabase> | null = null;

function openDb(): Promise<IDBDatabase> {
  if (dbPromise) return dbPromise;
  dbPromise = new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(STORE_TICKETS)) {
        db.createObjectStore(STORE_TICKETS, { keyPath: 'ticket_uuid' });
      }
      if (!db.objectStoreNames.contains(STORE_REDEEMED)) {
        db.createObjectStore(STORE_REDEEMED, { keyPath: 'ticket_uuid' });
      }
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
  return dbPromise;
}

function tx<T>(store: string, mode: IDBTransactionMode, fn: (s: IDBObjectStore) => IDBRequest<T>): Promise<T> {
  return openDb().then(
    (db) =>
      new Promise<T>((resolve, reject) => {
        const t = db.transaction(store, mode);
        const s = t.objectStore(store);
        const req = fn(s);
        req.onsuccess = () => resolve(req.result);
        req.onerror = () => reject(req.error);
      }),
  );
}

export async function ticketCount(): Promise<number> {
  return tx<number>(STORE_TICKETS, 'readonly', (s) => s.count());
}

export async function storeTickets(tickets: OfflineTicket[]): Promise<void> {
  const db = await openDb();
  await new Promise<void>((resolve, reject) => {
    const t = db.transaction(STORE_TICKETS, 'readwrite');
    const s = t.objectStore(STORE_TICKETS);
    for (const tk of tickets) s.put(tk);
    t.oncomplete = () => resolve();
    t.onerror = () => reject(t.error);
  });
}

// Pop the oldest non-expired ticket. Atomically remove it from tickets and
// insert into redeemed with the current wall clock as played_at.
// Returns null if there's no usable ticket (empty or all expired).
export async function popTicketForPlay(): Promise<OfflineTicket | null> {
  const db = await openDb();
  return new Promise((resolve, reject) => {
    const t = db.transaction([STORE_TICKETS, STORE_REDEEMED], 'readwrite');
    const tickets = t.objectStore(STORE_TICKETS);
    const redeemed = t.objectStore(STORE_REDEEMED);
    const now = Math.floor(Date.now() / 1000);
    let picked: OfflineTicket | null = null;

    const cursorReq = tickets.openCursor();
    cursorReq.onsuccess = () => {
      const cursor = cursorReq.result;
      if (!cursor) {
        // No more entries; commit by closing transaction. picked may still be null.
        return;
      }
      const tk = cursor.value as OfflineTicket;
      if (tk.expires_at <= now) {
        // Expired ticket — drop silently and continue searching.
        cursor.delete();
        cursor.continue();
        return;
      }
      picked = tk;
      cursor.delete();
      redeemed.put({ ticket_uuid: tk.ticket_uuid, played_at: now } satisfies Redemption);
    };
    t.oncomplete = () => resolve(picked);
    t.onerror = () => reject(t.error);
  });
}

export async function pendingRedemptions(): Promise<Redemption[]> {
  const db = await openDb();
  return new Promise((resolve, reject) => {
    const t = db.transaction(STORE_REDEEMED, 'readonly');
    const s = t.objectStore(STORE_REDEEMED);
    const req = s.getAll();
    req.onsuccess = () => resolve((req.result as Redemption[]) ?? []);
    req.onerror = () => reject(req.error);
  });
}

export async function clearRedeemed(uuids: string[]): Promise<void> {
  if (!uuids.length) return;
  const db = await openDb();
  await new Promise<void>((resolve, reject) => {
    const t = db.transaction(STORE_REDEEMED, 'readwrite');
    const s = t.objectStore(STORE_REDEEMED);
    for (const u of uuids) s.delete(u);
    t.oncomplete = () => resolve();
    t.onerror = () => reject(t.error);
  });
}
